import React, { useState } from 'react';
import { X, Tag } from 'lucide-react';
import clsx from 'clsx';
import PropTypes from 'prop-types';

const PromoCodeInput = ({ 
  onApply, 
  onRemove, 
  appliedPromoCode,
  appliedCode, 
  error, 
  isLoading,
  disabled 
}) => {
  const [code, setCode] = useState('');

  const handleApply = () => {
    if (code.trim()) {
      onApply(code.trim().toUpperCase());
    }
  };

  const handleRemove = (e) => {
    e.preventDefault();
    setCode('');
    onRemove();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !disabled && !isLoading) {
      handleApply();
    }
  };

  if (appliedPromoCode && !isLoading && !error) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-sm font-medium text-green-900">
                Promo Code Applied
              </p>
              <p className="text-sm text-green-700">{appliedCode}</p>
            </div>
          </div>
          <button
            onClick={handleRemove}
            disabled={disabled}
            className="p-1 hover:bg-green-100 rounded-full transition-colors"
            aria-label="Remove promo code"
          >
            <X className="w-5 h-5 text-green-700" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-col md:flex-row gap-3">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyPress={handleKeyPress}
          placeholder="Enter code"
          disabled={disabled || isLoading}
          className={clsx(
            'flex-1 px-4 py-3 text-base border-2 rounded-xl',
            'focus:outline-none focus:ring-0 focus:border-gray-900',
            'disabled:bg-gray-100 disabled:cursor-not-allowed',
            'placeholder:text-gray-400',
            error ? 'border-red-300' : 'border-gray-300'
          )}
        />
        <button
          type="button"
          onClick={handleApply}
          disabled={!code.trim() || disabled || isLoading}
          className={clsx(
            'px-8 py-3 rounded-xl font-semibold text-base transition-all',
            'border-2 border-gray-900',
            'min-w-[140px] flex items-center justify-center',
            !code.trim() || disabled || isLoading
              ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
              : 'bg-white text-gray-900 hover:bg-gray-50 active:bg-gray-100'
          )}
        >
          {isLoading ? (
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
          ) : (
            'Apply'
          )}
        </button>
      </div>
      {error && (
        <p className="text-sm text-red-600 flex items-center gap-1 px-1">
          <span>⚠</span> {error}
        </p>
      )}
    </div>
  );
};

PromoCodeInput.propTypes = {
  onApply: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  appliedPromoCode: PropTypes.bool,
  appliedCode: PropTypes.string,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool
};

PromoCodeInput.defaultProps = {
  appliedCode: null,
  error: null,
  isLoading: false,
  disabled: false
};

export default PromoCodeInput;